// Override Reasons — every line-item price override with a reason,
// grouped by reason string so managers can spot patterns.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function OverrideReasonsReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_month");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/override-reasons`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw numbers; discount signed negative.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const signedMoney = (v) => (v < 0 ? `- ${money(Math.abs(v))}` : money(v));
    const num = { numeric: true, format: money, align: "right" };
    const signedNum = { numeric: true, format: signedMoney, align: "right" };
    const rows = (data.rows || []).map((r) => ({
      when: fmtTime(r.at),
      item: r.item_name,
      list: Number(r.unit_price) || 0,
      override: Number(r.override_price) || 0,
      discount: -Math.abs(Number(r.discount) || 0),
      reason: r.reason,
      by: r.by_name,
      order: `#${r.order_number}`,
    }));
    return {
      filename: "override-reasons",
      range: { date_from: data.date_from, date_to: data.date_to },
      subtitle: `${data.reasons?.length || 0} distinct reason(s), total discount ${money(data.total_discount)}`,
      columns: [
        { header: "When", key: "when" },
        { header: "Item", key: "item" },
        { header: "List", key: "list", ...num },
        { header: "Override", key: "override", ...num },
        { header: "Discount", key: "discount", ...signedNum },
        { header: "Reason", key: "reason" },
        { header: "By", key: "by" },
        { header: "Order", key: "order" },
      ],
      rows,
      footerRow: ["Total discount", "", "", "", -Math.abs(Number(data.total_discount) || 0), "", "", ""],
    };
  }, [data, branch]);

  return (
    <ReportShell title="Override Reasons" subtitle="Every line-item price override applied inside the window, grouped by the reason the operator recorded." branchName={branch?.name} tab="sales" exportSpec={exportSpec}>
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="rounded-lg overflow-hidden lg:col-span-1" data-testid="override-reasons-summary">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border flex items-baseline justify-between">
                <h2 className="text-sm font-semibold">By Reason</h2>
                {data && (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">total discount</div>
                    <div className="text-lg font-semibold">- {formatBranchMoney(branch, data.total_discount)}</div>
                  </div>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="uppercase text-[10px] tracking-wider">Reason</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right">Count</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right">Discount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!data || data.reasons.length === 0) ? (
                    <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">
                      {loading ? "Loading…" : "No overrides."}
                    </TableCell></TableRow>
                  ) : data.reasons.map((r) => (
                    <TableRow key={r.reason} data-testid={`override-reason-${r.reason}`}>
                      <TableCell className="text-sm truncate max-w-xs" title={r.reason}>{r.reason}</TableCell>
                      <TableCell className="text-right text-sm">{r.count}</TableCell>
                      <TableCell className="text-right text-sm font-medium">- {formatBranchMoney(branch, r.total_discount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="rounded-lg overflow-hidden lg:col-span-2" data-testid="override-rows-card">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold">All Overrides</h2>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="uppercase text-[10px] tracking-wider">When</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Item</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right">List</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right">Override</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right">Discount</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Reason</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">By</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Order</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!data || data.count === 0) ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                      {loading ? "Loading…" : "No overrides in this range."}
                    </TableCell></TableRow>
                  ) : data.rows.map((r, i) => (
                    <TableRow key={`${r.order_id}-${i}`} data-testid={`override-row-${i}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtTime(r.at)}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate" title={r.item_name}>{r.item_name}</TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.unit_price)}</TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.override_price)}</TableCell>
                      <TableCell className="text-right text-sm text-rose-700">- {formatBranchMoney(branch, r.discount)}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate" title={r.reason}>{r.reason}</TableCell>
                      <TableCell className="text-xs">{r.by_name}</TableCell>
                      <TableCell className="text-xs">
                        <Link to={`/staff/branch/${bid}/orders?order=${r.order_id}`} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`override-order-link-${i}`}>
                          #{r.order_number} <ExternalLink className="w-3 h-3" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </ReportShell>
  );
}
